import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import StyledActionButton from 'components/layout/StyledActionButton';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import MediDataNotificationDetailsModal from 'components/medidata-notifications/MediDataNotificationDetailsModal';
import moment from 'moment';
import React, { useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Icon, Loader, Modal, Popup } from 'semantic-ui-react';
import axios from 'services/http';
import { getBillXmlRequestNotificationsFromUrl, putBillXmlRequestNotificationStatus } from 'services/mediDataServices';
import styled from 'styled-components';
import { MediDataNotificationsDto } from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StatusMsgDiv = styled.div`
  display: inline-flex;
  float: left;
  padding-top: 0.5rem;
  margin-left: 0.5rem;
`

const TableDiv = styled.div`
  flex: 1 1 auto;
  padding: 0.5rem;
  background: white;
`;

const ActionsDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 0.5rem;
  background: white;
  justify-content: space-between;

  & .btn-next {
    margin-left: auto;
  }
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const NoDataDiv = styled.div`
  font-size: 1rem;
  color: #444;
  font-style: italic;
  padding: 0.7rem 1rem;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.bodyColumn {
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
    white-space: normal;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }

  .edit-icon {
    padding-left: 8px;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps, MandateConsumerRenderProps,
    ConsultationsConsumerRenderProps {
  notificationData: MediDataNotificationsDto,
  notificationLoading: boolean,
  onFilterNotificationData: (notificationData: Partial<MediDataNotificationsDto>) => void,
  onNotificationLoadingChange: (notificationsLoading: boolean) => void
}

const MediDataNotificationsTableView = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {
    notificationData,
    notificationLoading,
    onFilterNotificationData,
    onNotificationLoadingChange,
    currentUser,
    getMessage
  } = props;

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [notificationIx, setNotificationIx] = useState(undefined as number | undefined);
  const [statusMessage, setStatusMessage] = useState(undefined as string | undefined);

  const setNotificationStatus = async (notificationId: number) => {
    try {
      let mandateId = currentUser!.userConfig.mandateId;
      const response = await putBillXmlRequestNotificationStatus(
          mandateId, notificationId, cancelTokenSource);

      const notifications = notificationData.notifications.filter(n => n.id !== notificationId);
      const notifData: MediDataNotificationsDto = {
        ...notificationData,
        notifications
      }
      setStatusMessage(getMessage("mediDataNotifications.changeStatusSuccess"));
      setTimeout(() => {
        setDetailsModalOpen(false);
        setStatusMessage(undefined);
      }, 3000)

      onFilterNotificationData(notifData);
    } catch (e) {
      setStatusMessage(getMessage("mediDataNotifications.changeStatusError"))
    } finally {

    }
  }

  const onChangePage = (rel: string, url: string) => {

    onNotificationLoadingChange(true);

    let mandateId = props.currentUser!.userConfig.mandateId;
    getBillXmlRequestNotificationsFromUrl(mandateId, rel, url, cancelTokenSource)
        .then(response => {
          onFilterNotificationData(response)
        })
        .finally(() => {
          onNotificationLoadingChange(false)
        });
  };

  const onDetailsModalOpen = (notificationIx: number) => {
    setNotificationIx(notificationIx);
    setDetailsModalOpen(true);
  }

  const renderTable = (): JSX.Element => {

    return (
        <StyledTable
            rowGetter={notificationRowGetter()}
            rowRenderer={notificationRowRenderer()}
            rowCount={notificationData.notifications.length}
            rowHeight={52}
            columns={[
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataNotifications.tableHeader.billId"),
                dataKey: "correlationReference"
              },
              {
                width: 200,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataNotifications.tableHeader.created"),
                dataKey: "created",
                cellRenderer: dateCellRenderer
              },
              {
                width: 200,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataNotifications.tableHeader.severity"),
                dataKey: "severity",
                cellRenderer: severityCellRenderer
              },
              {
                width: 200,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediDataNotifications.tableHeader.errorCode"),
                dataKey: "errorCode",
                cellRenderer: errorCodeCellRenderer
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("details")),
                dataKey: "id",
                cellRenderer: detailsCellRenderer
              }
            ]}
        />
    )
  };

  const notificationRowGetter = () => ({index}: any) => {
    const notifications = notificationData.notifications;
    if (notifications[index]) {
      Object.assign(notifications[index], {index: index + 1});

      return notifications[index];
    }

    return {
      index: index + 1
    }
  };

  const notificationRowRenderer = () =>
      ({className, columns, index, key, style}: any) => {

        const a11yProps = {"aria-rowindex": index + 1};

        let styles = {...style};

        if (!notificationData.notifications[index].read) {
          style.fontWeight = 'bold';
          return (
              <div
                  {...a11yProps}
                  className={className}
                  key={key}
                  role="row"
                  style={style}
                  onDoubleClick={() => onDetailsModalOpen(index)}
              >
                {columns}
              </div>
          );

        } else {
          return (
              <div
                  {...a11yProps}
                  className={className}
                  key={key}
                  role="row"
                  style={styles}
                  onDoubleClick={() => onDetailsModalOpen(index)}
              >
                {columns}
              </div>
          );
        }
      };

  const dateCellRenderer = (data: any) => {

    const {cellData} = data;

    if (moment(cellData).isValid()) {
      return moment(cellData).format("DD.MM.YYYY / HH:mm")
    }

    return "";
  };

  const severityCellRenderer = (data: any) => {

    const {cellData} = data;

    if (cellData) {
      return getMessage(`mediDataNotifications.severity.${cellData}`);
    }
    return "";
  };

  const errorCodeCellRenderer = (data: any) => {

    const {rowIndex, cellData} = data;

    if (cellData) {
      const subject = notificationData.notifications[rowIndex].subject[props.language];
      if (subject) {
        return (
            <div>
              <div>{subject}</div>
              <div>{cellData}</div>
            </div>
        )
      }
      return <div>{cellData}</div>
    } else {
      return ""
    }
  };

  const detailsCellRenderer = (data: any) => {

    const {rowIndex, cellData} = data;

    return (
        <>
          <StyledActionButton icon
                              type="button"
                              onClick={() => onDetailsModalOpen(rowIndex)}
          >
            <Popup
                trigger={<Icon name={'info circle'}/>}
                content={getMessage("details")}
                size="small"
                position="top center"
                style={PopupStyle}
                inverted
            />
          </StyledActionButton>
        </>
    )
  };

  const renderLoader = (): JSX.Element => {

    return (
        <Loader active={true} size="small">
          {`${getMessage("loading")} ${getMessage("header.navigation.mediDataNotifications").toLowerCase()}`}
        </Loader>
    )
  };

  const renderDetailsModal = () => {

    if (!detailsModalOpen && notificationData.notifications.length) {
      return <></>;
    }

    // @ts-ignore
    const notification = notificationData.notifications[notificationIx];

    if (notification) {
      return (
          <Modal
              open={detailsModalOpen}
              onClose={() => setDetailsModalOpen(false)}
              closeOnDimmerClick={true}
          >
            <Modal.Header>
              {getMessage("mediDataNotifications.notificationDetails")} [{notification.correlationReference}]
            </Modal.Header>
            <FormModalContentContainer>
              <MediDataNotificationDetailsModal
                  notification={notification}
                  language={props.language}
              />
            </FormModalContentContainer>
            <Modal.Actions>
              <Button floated="left" compact
                      primary onClick={() => setNotificationStatus(notification)}>
                {getMessage("mediDataNotifications.confirmStatus")}
              </Button>
              {<StatusMsgDiv>{statusMessage}</StatusMsgDiv>}
              <Button compact onClick={() => setDetailsModalOpen(false)}>
                {getMessage("button.close")}
              </Button>
            </Modal.Actions>
          </Modal>
      );
    }

    return <></>;
  }

  if (notificationLoading) {
    return renderLoader();
  }

  return (
      <>
        <TableDiv>
          {(notificationData && notificationData.notifications && notificationData.notifications.length > 0) ?
              (renderTable()) : (<NoDataDiv>{getMessage("mediDataNotifications.noNotifications")}</NoDataDiv>)
          }
        </TableDiv>
        <ActionsDiv>
          {notificationData && notificationData.previous &&
              <Button compact type="button" secondary
                      onClick={() => onChangePage("previous", notificationData.previous)}
              >
                {getMessage("mediDataNotifications.button.previous")}
              </Button>
          }
          {notificationData && notificationData.next &&
              <Button compact type="button" className="btn-next" secondary
                      onClick={() => onChangePage("next", notificationData.next)}
              >
                {getMessage("mediDataNotifications.button.next")}
              </Button>
          }
        </ActionsDiv>
        {renderDetailsModal()}
      </>
  );
}

export default withMessages(withAuthContext(withRouterWorkaround(withMandateContext(
    withConsultationsContext(MediDataNotificationsTableView)))));
